import { walutaDlaKraju } from '@insolitum/types';
import {
  Package,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ShoppingCart,
  ExternalLink,
  Star,
  Zap,
  Shield,
  RefreshCw,
  Brain,
  Power,
  PowerOff,
  X,
  HardHat,
  Target,
  Sparkles,
  Search,
  Filter,
  Download,
  Tag,
  ChevronRight,
  CreditCard,
  Settings,
  ArrowUpRight,
  Plug,
  ShoppingBag,
  Receipt,
  Store,
  Scale,
  Truck,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ZaplanowanaZmianaPlanu } from '../../components/admin/ZaplanowanaZmianaPlanu';
import { usePotwierdzenieZmianCennika } from '../../components/cennik/PotwierdzenieZmianyCennika';
import { useBilling } from '../../hooks/useBilling';
import { useMarketplace } from '../../hooks/useMarketplace';
import { linijkaBrutto, sufiksNetto } from '../../lib/cenaOprogramowania';
import {
  etykietaCeny,
  filtrujUrzadzenia,
  kategoriePolki,
  opisMetrologii,
  czyPolkaUrzadzen,
  polkaModulu,
  polkaUrzadzenia,
  razemBrutto,
  stanZakupu,
  zdjeciaOferty,
  type PolkaMarketplace,
  type UrzadzenieOferty,
} from '../../lib/marketplaceDevices';
import {
  akcjaWylaczonegoModulu,
  bladPrzelaczeniaModulu,
  bladTriala,
  bladWlaczenia,
} from '../../lib/moduleTrial';
import {
  blokadaPakietu,
  blokadaPlanu,
  useOcenyPakietow,
  useOcenyPlanu,
} from '../../lib/ocenaPlanuModulu';
import { SHELL_EVENTS, publishShellEvent } from '../../lib/shellEvents';
import { supabase } from '../../lib/supabase';
import { ORGANY_W_POWLOCE, sciezkaModulu } from '../../modules/organWPowloce';
import { MODULE_REGISTRY } from '../../modules/registry';
import { moduleMaWlasnyEkran } from '../../modules/rodzajModulu';
import { useAuth } from '../../providers/AuthProvider';
import { useModuleSubscriptionStore } from '../../stores/moduleSubscriptionStore';

import { PolaczWtyczke, pobierzStanKonektorow, type StanKonektora } from './PolaczWtyczke';
import { PrzyciskProbyModulu } from './PrzyciskProbyModulu';
import { UrzadzenieSzczegoly } from './UrzadzenieSzczegoly';

interface Module {
  id: string;
  name: string;
  description: string;
  long_description?: string;
  icon: string;
  highlight_features_pl?: string[];
  /** `organ` albo `integration` (migracja 450); brak czytamy jako `organ`. */
  module_kind?: string;
  status: string;
  category?: string;
  tags?: string[];
  avg_rating?: number;
  review_count?: number;
  total_installs?: number;
  featured?: boolean;
  version?: string;
  /** Klucz licencji ilościowej (Receptor: `serwery_receptora`) — kupuje się liczbę sztuk. */
  klucz_ilosci?: string | null;
}

interface TierPrice {
  id: string;
  amount: number;
  currency: string;
  billing_type: string;
  features_pl: string[];
  limits: Record<string, number>;
  module_pricing_tiers: {
    name: string;
    name_pl: string;
    slug: string;
    sort_order: number;
    is_default: boolean;
    badge_text_pl?: string;
  };
}

interface ModuleSubscription {
  id: string;
  module_id: string;
  status: string;
  plan: string;
  /** Próg kupiony w cenniku (migracja 823); NULL dla nadań — wtedy `plan`. */
  tier_slug?: string | null;
  trial_ends_at: string | null;
  subscription_starts_at: string | null;
  subscription_ends_at: string | null;
  created_at: string;
  /** Właściciel-subskrypcja Stripe i jej surowy status (naprawa kasy 02.10.2026). */
  stripe_subscription_id?: string | null;
  stripe_status?: string | null;
  /** Kto wyłączył moduł: `tenant` albo `super_admin`. */
  suspended_by?: string | null;
  /** Zaplanowana obniżka od następnego okresu (runda 4, harmonogram Stripe). */
  zmiana_planu_na?: string | null;
  zmiana_planu_okres?: string | null;
  zmiana_planu_od?: string | null;
  /** Pakiet sprzed zaplanowanej zmiany (runda 5) — ten sam = zmienia się tylko okres. */
  zmiana_planu_z?: string | null;
}

interface PricingTierInfo {
  slug: string;
  name: string;
  name_pl: string;
}

// Module display metadata (icons, colors, branded names) — derived from the
// single module registry (apps/universe-shell/src/modules/registry.ts) so the
// sidebar, mobile nav and this page all share one source of truth.
//
// Drugim źródłem są organy montowane W POWŁOCE (`organWPowloce.ts`) — dziś
// druk. Nie ma ich w `MODULE_REGISTRY`, bo ten rejestr generuje też nawigację
// i trasy iframe (patrz komentarz w `organWPowloce.ts`), a bez wpisu tutaj
// karta opłaconego organu rysowałaby się szarym `Package` bez nazwy handlowej.
const MODULE_ICONS: Record<string, (typeof MODULE_REGISTRY)[number]['icon']> = Object.fromEntries([
  ...MODULE_REGISTRY.map((m) => [m.slug, m.icon] as const),
  ...ORGANY_W_POWLOCE.map((o) => [o.slug, o.icon] as const),
]);

const MODULE_COLORS: Record<string, { bg: string; text: string; border: string }> =
  Object.fromEntries([
    ...MODULE_REGISTRY.map((m) => [m.slug, m.color] as const),
    ...ORGANY_W_POWLOCE.map((o) => [o.slug, o.color] as const),
  ]);

const MODULE_DISPLAY_NAMES: Record<string, string> = Object.fromEntries([
  ...MODULE_REGISTRY.map((m) => [m.slug, m.displayName] as const),
  ...ORGANY_W_POWLOCE.map((o) => [o.slug, o.displayName] as const),
]);

// Star rating renderer
function StarRating({ rating, count }: { rating: number; count: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`}
          />
        ))}
      </div>
      <span className="text-xs text-gray-500">
        {rating.toFixed(1)} ({count})
      </span>
    </div>
  );
}

// Category icon mapping for marketplace
const CATEGORY_ICONS: Record<string, typeof Brain> = {
  hr: Calendar,
  iot: Brain,
  production: HardHat,
  finance: Star,
  analytics: Target,
  communication: Sparkles,
  compliance: Shield,
  ai: Sparkles,
  other: Package,
  // Kategorie polki wtyczek i polki urzadzen (487).
  integrations: Plug,
  ecommerce: ShoppingBag,
  accounting: Receipt,
  pos: Store,
  weighing: Scale,
  energy: Zap,
  logistics: Truck,
};

type SortOption = 'popular' | 'newest' | 'rating' | 'price';

/**
 * Zdjecie katalogowe oferty sprzetowej (487).
 *
 * Wlasny komponent wylacznie dla `onError`: adres zdjecia pochodzi z `photos`
 * i wskazuje poza nasza domene, wiec moze przestac odpowiadac bez zadnego
 * zwiazku z naszym wdrozeniem. Ikona zepsutego pliku obok ceny za 21 tys.
 * mowi o katalogu cos, czego nie chcemy mowic — wiec kafelek po prostu chowa
 * ramke ze zdjeciem i wyglada jak kazdy inny bez fotki.
 */
function ZdjecieOferty({ src, alt }: { src: string; alt: string }) {
  const [doszlo, setDoszlo] = useState(true);
  if (!doszlo) return null;

  return (
    <div className="bg-gray-900/40 border-b border-gray-700 flex items-center justify-center h-40">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setDoszlo(false)}
        className="max-h-40 w-auto object-contain"
      />
    </div>
  );
}

export function ModulesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [modules, setModules] = useState<Module[]>([]);
  const [subscriptions, setSubscriptions] = useState<ModuleSubscription[]>([]);
  // Stan podłączenia wtyczek integracyjnych (453), po `kind`. Pusty dla kogoś
  // bez roli administracyjnej — RPC odmawia, a sekcja połączenia się nie pokaże.
  const [stanKonektorow, setStanKonektorow] = useState<Record<string, StanKonektora>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activatingModule, setActivatingModule] = useState<string | null>(null);
  const [disablingModule, setDisablingModule] = useState<string | null>(null);
  const [enablingModule, setEnablingModule] = useState<string | null>(null);
  const [showDisableModal, setShowDisableModal] = useState<Module | null>(null);

  // Pricing from DB (module_prices table)
  const [lowestPriceMap, setLowestPriceMap] = useState<Record<string, number>>({});
  const [nextTierPriceMap, setNextTierPriceMap] = useState<Record<string, number>>({});
  const [priceCurrencyMap, setPriceCurrencyMap] = useState<Record<string, string>>({});

  // Marketplace features
  // Polka oferty: oprogramowanie, wtyczki albo urzadzenia (391 + 487). To NIE
  // jest kategoria dziedzinowa — waga jest jednoczesnie „Wagi i wazenie"
  // i urzadzeniem, wiec te dwie osie musza byc osobne. Przelacznik zmienia
  // ZRODLO listy: urzadzenia mieszkaja we wlasnej tabeli, a wtyczki sa wierszami
  // `modules` z `module_kind = 'integration'` — cudzym systemem, nie organem
  // z wlasnym ekranem, wiec mieszanie ich z modulami mylilo obie strony.
  const [polka, setPolka] = useState<PolkaMarketplace>('software');
  const [urzadzenia, setUrzadzenia] = useState<UrzadzenieOferty[]>([]);
  const [detalUrzadzenia, setDetalUrzadzenia] = useState<UrzadzenieOferty | null>(null);
  /** Ilosc wybrana na karcie oferty, per slug. Domyslnie jedna sztuka. */
  const [iloscSztuk, setIloscSztuk] = useState<Record<string, number>>({});
  /** Slug pozycji, dla ktorej trwa otwieranie kasy — blokuje podwojne klikniecie. */
  const [kupowanaOferta, setKupowanaOferta] = useState<string | null>(null);
  const [bladZakupu, setBladZakupu] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sort, setSort] = useState<SortOption>('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [detailModule, setDetailModule] = useState<Module | null>(null);
  const [purchaseModule, setPurchaseModule] = useState<Module | null>(null);
  const [purchaseTiers, setPurchaseTiers] = useState<TierPrice[]>([]);
  const [purchaseAllTiers, setPurchaseAllTiers] = useState<TierPrice[]>([]);
  const [purchaseBillingType, setPurchaseBillingType] = useState<'monthly' | 'yearly'>('monthly');
  // Liczba serwerów modułu ilościowego (Receptor, D12: 1..20).
  const [purchaseIlosc, setPurchaseIlosc] = useState(1);
  const [zmianaIlosciInfo, setZmianaIlosciInfo] = useState<string | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const { categories } = useMarketplace();
  const { potwierdz: potwierdzZmiany, okno: oknoZmianCennika } = usePotwierdzenieZmianCennika();
  const {
    createModuleCheckoutSession,
    changeModuleQuantity,
    createDeviceCheckoutSession,
    openBillingPortal,
    cancelScheduledChange,
    maKlientaStripe,
  } = useBilling();
  const [tierNameMap, setTierNameMap] = useState<Record<string, PricingTierInfo>>({});
  const [openingPortal, setOpeningPortal] = useState(false);
  /** Moduł, którego zaplanowaną zmianę właśnie odwołujemy (runda 5). */
  const [odwolywanaZmiana, setOdwolywanaZmiana] = useState<string | null>(null);

  // Get store action to notify sidebar about module toggle
  const refreshSidebarAfterToggle = useModuleSubscriptionStore((state) => state.refreshAfterToggle);

  /**
   * Kup sprzęt: otwórz kasę Stripe na wybraną pozycję i ilość.
   *
   * Ekran NIE przesyła kwoty — tylko slug i ilość. Cena, stawka VAT i koszt
   * własny (a przez to podstawa prowizji partnera) są sprawą katalogu po
   * stronie serwera; przeglądarka nie ma tu głosu.
   */
  const handleKupUrzadzenie = useCallback(
    async (d: UrzadzenieOferty) => {
      setBladZakupu(null);
      setKupowanaOferta(d.slug);
      try {
        const url = await createDeviceCheckoutSession([
          { slug: d.slug, quantity: iloscSztuk[d.slug] ?? 1 },
        ]);
        if (url) {
          window.location.href = url;
          return;
        }
        // `null` znaczy, że backend odmówił i powiedział dlaczego — a to jest
        // zdanie dla kupującego, nie wpis w konsoli.
        setBladZakupu(`Nie udało się otworzyć płatności za „${d.name}".`);
      } catch (err) {
        setBladZakupu((err as Error).message);
      } finally {
        setKupowanaOferta(null);
      }
    },
    [createDeviceCheckoutSession, iloscSztuk]
  );

  const fetchModulesAndSubscriptions = useCallback(async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      setError(null);

      const [modulesRes, subscriptionsRes, pricesRes, tiersRes, devicesRes] = await Promise.all([
        supabase
          .from('modules')
          .select(
            'id, name, description, long_description, icon, status, category, tags, avg_rating, review_count, total_installs, featured, version, highlight_features_pl, module_kind, klucz_ilosci'
          )
          .eq('status', 'active')
          .eq('is_public', true),
        supabase
          .from('module_subscriptions')
          .select('*')
          .eq('organization_id', currentOrganization.organization_id),
        // Fetch monthly prices for card display (any currency)
        supabase
          .from('module_prices')
          .select('module_id, amount, currency, module_pricing_tiers(sort_order)')
          .eq('billing_type', 'monthly')
          .eq('is_active', true),
        // Fetch pricing tier definitions for plan name display
        supabase.from('module_pricing_tiers').select('slug, name, name_pl'),
        // Oferty sprzetowe (391). Metrologia przychodzi ZE ZLACZENIA z rejestrem
        // modeli — oferta nie ma wlasnej dzialki ani zakresu i mieć nie bedzie,
        // zeby sklep nie stal sie druga prawda o tej samej wadze.
        supabase
          .from('marketplace_devices')
          .select(
            'id, slug, name, category, short_description, long_description, photos, ' +
              'price_kind, price_net, currency, vat_rate, lead_time_days, sku, ' +
              'stripe_price_active, ' +
              'fabrica_instrument_models(code, size_label, mounting, resolution, range_max, measures_uom_code)'
          )
          .eq('is_active', true)
          .order('sort_order')
          .order('name'),
      ]);

      if (modulesRes.error) throw modulesRes.error;

      // Blad ofert sprzetowych NIE przewraca calego ekranu: moduly sa tu rzecza
      // glowna, a brak katalogu urzadzen ma pokazac pusta polke, nie czerwony pas
      // zamiast wszystkiego.
      if (devicesRes.error) {
        console.warn('marketplace_devices:', devicesRes.error.message);
        setUrzadzenia([]);
      } else {
        setUrzadzenia((devicesRes.data ?? []) as unknown as UrzadzenieOferty[]);
      }

      // Parse modules from DB
      const dbModules: Module[] = (modulesRes.data || []).map((dbm: any) => {
        // Ensure tags is always a proper array (DB may return JSON string)
        const parsedTags = Array.isArray(dbm.tags)
          ? dbm.tags
          : typeof dbm.tags === 'string'
            ? (() => {
                try {
                  const p = JSON.parse(dbm.tags);
                  return Array.isArray(p) ? p : [];
                } catch {
                  return [];
                }
              })()
            : [];
        // Ensure highlight_features_pl is always a proper array
        const parsedFeatures = Array.isArray(dbm.highlight_features_pl)
          ? dbm.highlight_features_pl
          : [];
        return { ...dbm, tags: parsedTags, highlight_features_pl: parsedFeatures };
      });

      // Build price maps from module_prices table
      // Group by module → currency → prices, then pick best currency per module
      const lowest: Record<string, number> = {};
      const nextTier: Record<string, number> = {};
      const currencyMap: Record<string, string> = {};
      const pricesByModuleCurrency: Record<
        string,
        Record<string, { amount: number; sortOrder: number }[]>
      > = {};

      // Waluta kraju organizacji decyduje, który wiersz cennika pokazujemy
      // (PL → PLN). Wcześniej wybór szedł stałą listą priorytetów.
      const { data: orgRow } = await supabase
        .from('organizations')
        .select('country')
        .eq('id', currentOrganization.organization_id)
        .maybeSingle();
      const walutaOrganizacji = walutaDlaKraju(orgRow?.country);

      (pricesRes.data || []).forEach((p: any) => {
        const moduleId = p.module_id;
        const currency = p.currency || 'PLN';
        const sortOrder = p.module_pricing_tiers?.sort_order ?? 0;
        if (!pricesByModuleCurrency[moduleId]) pricesByModuleCurrency[moduleId] = {};
        if (!pricesByModuleCurrency[moduleId][currency])
          pricesByModuleCurrency[moduleId][currency] = [];
        pricesByModuleCurrency[moduleId][currency].push({ amount: p.amount, sortOrder });
      });

      // Preferuj walutę kraju organizacji (PL → PLN); dalej kanon i reszta.
      const CURRENCY_PRIORITY = [walutaOrganizacji, 'PLN', 'EUR', 'USD'];
      Object.entries(pricesByModuleCurrency).forEach(([moduleId, currencies]) => {
        const availableCurrencies = Object.keys(currencies);
        const bestCurrency =
          CURRENCY_PRIORITY.find((c) => availableCurrencies.includes(c)) || availableCurrencies[0];
        if (!bestCurrency) return;

        const prices = currencies[bestCurrency].sort((a, b) => a.sortOrder - b.sortOrder);
        if (prices.length > 0) lowest[moduleId] = prices[0].amount;
        if (prices.length > 1) nextTier[moduleId] = prices[prices.length - 1].amount;
        currencyMap[moduleId] = bestCurrency;
      });

      setLowestPriceMap(lowest);
      setNextTierPriceMap(nextTier);
      setPriceCurrencyMap(currencyMap);
      setModules(dbModules);
      setSubscriptions(subscriptionsRes.data || []);

      // Build tier slug → name map for displaying plan names
      const tMap: Record<string, PricingTierInfo> = {};
      (tiersRes.data || []).forEach((t: any) => {
        tMap[t.slug] = { slug: t.slug, name: t.name, name_pl: t.name_pl };
      });
      setTierNameMap(tMap);
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError('Nie udało się załadować modułów');
    } finally {
      setLoading(false);
    }
  }, [currentOrganization]);

  /**
   * Stan podłączenia wtyczek. Osobno od `fetchModulesAndSubscriptions`, bo idzie
   * INNĄ drogą — RPC `genesis_konektory_najemcy` z granicą roli w ciele funkcji,
   * nie zapytaniem do tabeli — i bo po samym połączeniu nie ma sensu przeładowywać
   * cennika ani ofert sprzętowych.
   */
  const odswiezKonektory = useCallback(async () => {
    const orgId = currentOrganization?.organization_id;
    if (!orgId) {
      setStanKonektorow({});
      return;
    }
    const wiersze = await pobierzStanKonektorow(orgId);
    setStanKonektorow(Object.fromEntries(wiersze.map((w) => [w.kind, w])));
  }, [currentOrganization]);

  useEffect(() => {
    fetchModulesAndSubscriptions();
  }, [fetchModulesAndSubscriptions]);

  useEffect(() => {
    odswiezKonektory();
  }, [odswiezKonektory]);

  // Ocena planu Universe per moduł (migracja 754). `null` = nie wiadomo —
  // wtedy przyciski zostają czynne, a odmowę i tak powie baza (HINT).
  const ocenyPlanu = useOcenyPlanu(currentOrganization?.organization_id, subscriptions);
  // Próg planu per pakiet (848): okno zakupu wyłącza pakiet powyżej planu organizacji.
  const ocenyPakietow = useOcenyPakietow(
    currentOrganization?.organization_id,
    purchaseModule?.id,
    [
      ...new Set(
        purchaseAllTiers
          .map((t) => t.module_pricing_tiers?.slug)
          .filter((s): s is string => typeof s === 'string')
      ),
    ].sort()
  );

  const getSubscription = (moduleId: string) => {
    return subscriptions.find((s) => s.module_id === moduleId);
  };

  /**
   * Próg subskrypcji do porównań z cennikiem: `plan` zlewa Start i Pro
   * w `starter`, więc kupiony próg czyta się z `tier_slug` (823).
   */
  const progSubskrypcji = (sub: ModuleSubscription) => sub.tier_slug ?? sub.plan;

  const getPlanDisplayName = (planSlug: string) => {
    if (!planSlug || planSlug === 'free') return 'Free';
    const tier = tierNameMap[planSlug];
    return tier?.name_pl || tier?.name || planSlug.charAt(0).toUpperCase() + planSlug.slice(1);
  };

  /** Nazwa pakietu modułu z cennika (`module_pricing_tiers.name_pl`) — ta sama, co w oknie zakupu. */
  const nazwaPakietu = (slug: string) =>
    tierNameMap[slug]?.name_pl || tierNameMap[slug]?.name || null;

  /** „Odwołaj zmianę" przy zaplanowanej obniżce (runda 5): moduł zostaje na obecnym pakiecie. */
  const handleOdwolajZmiane = async (moduleId: string) => {
    try {
      setOdwolywanaZmiana(moduleId);
      setError(null);
      await cancelScheduledChange(moduleId);
      await fetchModulesAndSubscriptions();
    } catch (err) {
      setError((err as Error).message || 'Nie udało się odwołać zmiany planu');
    } finally {
      setOdwolywanaZmiana(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningPortal(true);
      const url = await openBillingPortal();
      if (url) window.location.href = url;
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError('Nie udało się otworzyć portalu płatności');
    } finally {
      setOpeningPortal(false);
    }
  };

  const getSubscriptionStatus = (sub: ModuleSubscription | undefined) => {
    if (!sub) return { label: 'Nieaktywny', color: 'bg-gray-500/20 text-gray-400' };

    switch (sub.status) {
      case 'active':
        return { label: 'Aktywny', color: 'bg-green-500/20 text-green-400' };
      case 'trial':
        return { label: 'Trial', color: 'bg-amber-500/20 text-amber-400' };
      case 'suspended':
        return { label: 'Wyłączony', color: 'bg-orange-500/20 text-orange-400' };
      case 'cancelled':
        return { label: 'Anulowany', color: 'bg-gray-500/20 text-gray-400' };
      default:
        return { label: sub.status, color: 'bg-gray-500/20 text-gray-400' };
    }
  };

  const handleActivateTrial = async (moduleId: string) => {
    if (!currentOrganization) return;

    try {
      setActivatingModule(moduleId);

      // Trial zakłada SERWER (`module_trial_start`, migracja 467), nie ta strona.
      // Wcześniej powłoka wstawiała wiersz `module_subscriptions` wprost — a że
      // polityka RLS była `FOR ALL` z pustym `WITH CHECK`, dokładnie tą drogą
      // właściciel dowolnej organizacji mógł wpisać sobie `plan='enterprise'`.
      // Funkcja nie przyjmuje ani `plan`, ani `status`, ani daty końca: ustala
      // je sama i pilnuje, że trial przysługuje raz.
      const { data: wynik, error: trialError } = await supabase.rpc('module_trial_start', {
        p_organization_id: currentOrganization.organization_id,
        p_module_id: moduleId,
      });

      if (trialError) throw new Error(bladTriala(trialError.message, moduleId, trialError.hint));

      const trialEndsAt = (wynik as { trial_ends_at?: string } | null)?.trial_ends_at ?? null;

      void publishShellEvent(SHELL_EVENTS.moduleSubscriptionStarted, {
        module_id: moduleId,
        status: 'trial',
        plan: 'free',
        trial_ends_at: trialEndsAt,
      });

      fetchModulesAndSubscriptions();
      // Notify sidebar to refresh with animation
      await refreshSidebarAfterToggle(moduleId, 'enable');
    } catch (err) {
      console.error('Error activating trial:', err);
      const message = err instanceof Error ? err.message : 'Nie udało się aktywować wersji trial';
      setError(message);
    } finally {
      setActivatingModule(null);
    }
  };

  const handleOpenModule = (moduleId: string) => {
    const orgId = currentOrganization?.organization_id;
    // `sciezkaModulu` oddaje `/admin/modules/<id>` dla modułów w iframie, a dla
    // organu montowanego w powłoce — jego własny ekran (druk: /admin/printing).
    navigate(`${sciezkaModulu(moduleId)}${orgId ? `?org_id=${orgId}` : ''}`);
  };

  // ── Module purchase flow ────────────────────────────────────────────────────

  const handleOpenPurchaseModal = async (module: Module) => {
    // Bez płatnego planu okno zakupu też się otwiera: część pakietów jest dostępna
    // na planie Free (848: Mandatum START, enova), a każdy pakiet ocenia
    // `modul_wymagany_plan_pakietu` — pakiet powyżej planu jest wyłączony ze
    // zdaniem bazy, a kasa odmawia go jeszcze przed Stripe.
    setPurchaseModule(module);
    setPurchaseError(null);
    setPurchaseTiers([]);
    setPurchaseAllTiers([]);
    setPurchaseBillingType('monthly');

    try {
      // Fetch available pricing tiers for this module (monthly + yearly)
      const { data, error: fetchErr } = await supabase
        .from('module_prices')
        .select(
          'id, amount, currency, billing_type, features_pl, limits, module_pricing_tiers(name, name_pl, slug, sort_order, is_default, badge_text_pl)'
        )
        .eq('module_id', module.id)
        .in('billing_type', ['monthly', 'yearly'])
        .eq('is_active', true)
        .gt('amount', 0)
        .order('amount', { ascending: true });

      if (fetchErr) throw fetchErr;

      const allTiers = (data as unknown as TierPrice[]) || [];
      setPurchaseAllTiers(allTiers);
      // Default to monthly view
      setPurchaseTiers(allTiers.filter((t) => t.billing_type === 'monthly'));
    } catch (err) {
      console.error('Error fetching pricing tiers:', err);
      setPurchaseError('Nie udało się pobrać cennika dla tego modułu.');
    }
  };

  const handlePurchaseBillingTypeChange = (type: 'monthly' | 'yearly') => {
    setPurchaseBillingType(type);
    setPurchaseTiers(purchaseAllTiers.filter((t) => t.billing_type === type));
  };

  const handlePurchaseModule = async (moduleId: string, tierSlug: string, currency?: string) => {
    try {
      setPurchaseLoading(true);
      setPurchaseError(null);

      const checkoutUrl = await createModuleCheckoutSession({
        moduleId,
        tier: tierSlug,
        billingType: purchaseBillingType,
        currency,
        ...(purchaseModule?.klucz_ilosci ? { quantity: purchaseIlosc } : {}),
        potwierdz: potwierdzZmiany,
      });

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      const message = (err as Error).message || 'Nie udało się utworzyć sesji płatności';
      console.error('Error creating module checkout:', err);
      setPurchaseError(message);
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleZmienIlosc = async (moduleId: string) => {
    try {
      setPurchaseLoading(true);
      setPurchaseError(null);
      setZmianaIlosciInfo(null);
      const wynik = await changeModuleQuantity({ moduleId, quantity: purchaseIlosc });
      setZmianaIlosciInfo(
        wynik.stan === 'bez_zmian'
          ? `Licencja obejmuje już ${wynik.ilosc} serwerów.`
          : `Zmieniono z ${wynik.poprzednio} na ${wynik.ilosc} serwerów — licencja odświeży się po potwierdzeniu płatności.`
      );
    } catch (err) {
      setPurchaseError((err as Error).message || 'Nie udało się zmienić liczby serwerów');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleDisableModule = async (moduleId: string) => {
    if (!currentOrganization) return;

    try {
      setDisablingModule(moduleId);
      setError(null);

      // 1. Wyłączenie subskrypcji — przez serwer (migracja 467). Funkcja nie
      //    dotyka `plan`: „nie korzystam" nigdy nie ma prawa zmienić tego,
      //    za co najemca płaci.
      const { error: subError } = await supabase.rpc('module_subscription_set_enabled', {
        p_organization_id: currentOrganization.organization_id,
        p_module_id: moduleId,
        p_enabled: false,
      });

      if (subError) {
        setError(bladPrzelaczeniaModulu(subError, 'Nie udało się wyłączyć modułu'));
        return;
      }

      // 2. Update device licenses - remove module from their modules array
      // First, get all device licenses for this organization
      const { data: licenses, error: licensesError } = await supabase
        .from('device_licenses')
        .select('id, modules')
        .eq('organization_id', currentOrganization.organization_id);

      if (licensesError) throw licensesError;

      // Update each license to remove the module
      if (licenses && licenses.length > 0) {
        for (const license of licenses) {
          const currentModules = license.modules || [];
          // Check if this module (or related mobile modules) are in the license
          const moduleVariants = [moduleId, `${moduleId}_kiosk`, `${moduleId}_mobile`];
          const updatedModules = currentModules.filter((m: string) => !moduleVariants.includes(m));

          // Only update if modules array changed
          if (updatedModules.length !== currentModules.length) {
            // If no modules left, suspend the license
            const updateData: { modules: string[]; status?: string } = { modules: updatedModules };
            if (updatedModules.length === 0) {
              updateData.status = 'suspended';
            }

            await supabase.from('device_licenses').update(updateData).eq('id', license.id);
          }
        }
      }

      // (Krok 3 z poprzedniej wersji — powtórny UPDATE tej samej subskrypcji —
      //  zniknął. Robił dokładnie to samo, co krok 1, tylko po `id` zamiast po
      //  parze (organizacja, moduł); jedno wywołanie RPC załatwia oba.)

      void publishShellEvent(SHELL_EVENTS.moduleSubscriptionSuspended, {
        module_id: moduleId,
        status: 'suspended',
      });

      setShowDisableModal(null);
      fetchModulesAndSubscriptions();
      // Notify sidebar to refresh with animation
      await refreshSidebarAfterToggle(moduleId, 'disable');
    } catch (err) {
      console.error('Error disabling module:', err);
      setError('Nie udało się wyłączyć modułu');
    } finally {
      setDisablingModule(null);
    }
  };

  const handleEnableModule = async (moduleId: string) => {
    if (!currentOrganization) return;

    try {
      setEnablingModule(moduleId);
      setError(null);

      // Ponowne włączenie — serwerem (migracja 467). Status wynikowy ustala
      // funkcja z danych (trwający trial → 'trial', inaczej 'active'), a nie
      // ta strona; powłoka tylko go odczytuje z odpowiedzi.
      const subscription = subscriptions.find((s) => s.module_id === moduleId);

      if (subscription) {
        const { data: wynik, error: subError } = await supabase.rpc(
          'module_subscription_set_enabled',
          {
            p_organization_id: currentOrganization.organization_id,
            p_module_id: moduleId,
            p_enabled: true,
          }
        );

        if (subError) {
          // Odmowa bazy niesie zdanie dla człowieka (wygasły okres, próba po
          // terminie, plan za niski, licencja opiekuna) — nie zastępujemy go
          // ogólnikiem. Odmowy strażników (migracja 754) mają zdanie w HINT;
          // odmowy samej funkcji (naprawa kasy, 751) — słownik `bladWlaczenia`.
          setError(bladPrzelaczeniaModulu(subError, bladWlaczenia(subError.message ?? '')));
          return;
        }

        void publishShellEvent(SHELL_EVENTS.moduleSubscriptionResumed, {
          module_id: moduleId,
          status: (wynik as { status?: string } | null)?.status ?? 'active',
        });
      }

      fetchModulesAndSubscriptions();
      // Notify sidebar to refresh with animation
      await refreshSidebarAfterToggle(moduleId, 'enable');
    } catch (err) {
      console.error('Error enabling module:', err);
      // Odmowy funkcji (moduł nieopłacony, po końcu dostępu) mają powiedzieć
      // człowiekowi, co dalej — nie „coś poszło nie tak".
      setError(bladWlaczenia((err as { message?: string } | null)?.message ?? ''));
    } finally {
      setEnablingModule(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  // Podzial `modules` na dwie polki (487). Wtyczka nie jest modulem-organem:
  // nie ma wlasnego ekranu, tylko podpina cudzy system — mieszanie ich na
  // jednej liscie znaczylo, ze „Shopify" stal obok „FABRICA" i obiecywal to samo.
  const modulyPolki = modules.filter((m) => polkaModulu(m) === polka);

  // Filter modules based on search and category
  const filteredModules = modulyPolki.filter((m) => {
    if (search) {
      const q = search.toLowerCase();
      const displayName = (MODULE_DISPLAY_NAMES[m.id] || m.name).toLowerCase();
      const desc = m.description.toLowerCase();
      if (!displayName.includes(q) && !desc.includes(q) && !m.id.toLowerCase().includes(q)) {
        return false;
      }
    }
    if (selectedCategory !== 'all' && m.category !== selectedCategory) {
      return false;
    }
    return true;
  });

  // Urzadzenia idą przez WŁASNY filtr, bo szukamy w nich takze po kodzie modelu
  // z rejestru — operator ma na wadze naklejke `W-100-60-20g` i tego szuka.
  // Najpierw POLKA, potem szukanie i kategoria. Polka jest podzialem sprzetu na
  // trzy rozlaczne rodziny (IoT, wagi, panele), a kategoria dziedzinowa dziala
  // wewnatrz niej — te osie sa prostopadle i mieszanie ich dawaloby wagi na
  // polce paneli.
  const urzadzeniaPolki = czyPolkaUrzadzen(polka)
    ? urzadzenia.filter((d) => polkaUrzadzenia(d.category) === polka)
    : [];
  const urzadzeniaWidoczne = filtrujUrzadzenia(urzadzeniaPolki, search, selectedCategory);

  // Kategorie ZAWEZONE DO POLKI (487). Do tej pory nad lista urządzeń wisiał
  // rząd kategorii modułowych („Zasoby ludzkie", „Komunikacja"), z których ani
  // jedna nie zawężała niczego — filtr, który zawsze daje pustą półkę, uczy
  // tylko tego, żeby go nie używać.
  const kategorieWidoczne = kategoriePolki(categories, polka);

  return (
    <div className="space-y-6">
      {oknoZmianCennika}
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-aurora-text flex items-center gap-3">
            <Package className="w-7 h-7 text-purple-400" />
            {t('admin.modules.title', 'Moduły')}
          </h1>
          <p className="text-gray-400 mt-1">
            Odkryj, aktywuj i zarządzaj modułami INSOLITUM Universe
          </p>
        </div>
        <button
          onClick={fetchModulesAndSubscriptions}
          disabled={loading}
          className="p-2 bg-gray-800 border border-gray-700 rounded-ids-md text-gray-400 hover:text-aurora-text hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Polka oferty — oprogramowanie / wtyczki / urzadzenia (391 + 487).
          Stoi NAD wyszukiwarka i kategoriami, bo zmienia zrodlo listy, a nie
          jest kolejnym filtrem w obrebie tej samej polki.

          Przy zmianie polki kategoria wraca do „Wszystkie": kategorie sa
          zawezone do polki, wiec zostawiona „Wagi i wazenie" po przejsciu na
          oprogramowanie dawalaby pusty ekran bez widocznej przyczyny — filtr
          bylby aktywny, ale jego przycisku juz by nie bylo w rzedzie. */}
      <div className="flex flex-wrap gap-2">
        {(
          [
            [
              'software',
              'Oprogramowanie',
              Package,
              modules.filter((m) => polkaModulu(m) === 'software').length,
            ],
            [
              'plugin',
              'Wtyczki i integracje',
              Plug,
              modules.filter((m) => polkaModulu(m) === 'plugin').length,
            ],
            [
              'iot',
              'Urządzenia IoT',
              HardHat,
              urzadzenia.filter((d) => polkaUrzadzenia(d.category) === 'iot').length,
            ],
            [
              'wagi',
              'Wagi',
              HardHat,
              urzadzenia.filter((d) => polkaUrzadzenia(d.category) === 'wagi').length,
            ],
            [
              'panele',
              'Panele',
              HardHat,
              urzadzenia.filter((d) => polkaUrzadzenia(d.category) === 'panele').length,
            ],
          ] as const
        ).map(([wartosc, etykieta, Ikona, ile]) => (
          <button
            key={wartosc}
            onClick={() => {
              setPolka(wartosc);
              setSelectedCategory('all');
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-ids-md text-sm font-medium transition-colors ${
              polka === wartosc
                ? 'bg-purple-500 text-aurora-on-accent'
                : 'bg-gray-800 text-gray-400 hover:text-aurora-text border border-gray-700'
            }`}
          >
            <Ikona className="w-4 h-4" />
            {etykieta}
            <span className={`text-xs ${polka === wartosc ? 'text-purple-100' : 'text-gray-500'}`}>
              {ile}
            </span>
          </button>
        ))}
      </div>

      {/* Search & Filters Bar */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={
              czyPolkaUrzadzen(polka)
                ? 'Szukaj urządzeń...'
                : polka === 'plugin'
                  ? 'Szukaj wtyczek...'
                  : 'Szukaj modułów...'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-ids-md text-aurora-text placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-ids-md border transition-colors ${
            showFilters
              ? 'bg-purple-500/20 border-purple-500/30 text-purple-400'
              : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-aurora-text'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filtry
        </button>
        {/* Sortowanie dotyczy modulow (oceny, instalacje, progi cenowe). Przy
            urzadzeniach nie ma czego nim ustawiac, a kontrolka, ktora nic nie
            robi, jest obietnica bez pokrycia — wiec znika, zamiast udawac. */}
        {!czyPolkaUrzadzen(polka) && (
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            className="px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-ids-md text-aurora-text focus:outline-none focus:border-purple-500"
          >
            <option value="popular">Najpopularniejsze</option>
            <option value="newest">Najnowsze</option>
            <option value="rating">Najlepiej oceniane</option>
            <option value="price">Cena rosnąco</option>
          </select>
        )}
      </div>

      {/* Category Filters — zawezone do polki (487) */}
      {showFilters && kategorieWidoczne.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-purple-500 text-aurora-on-accent'
                : 'bg-gray-800 text-gray-400 hover:text-aurora-text border border-gray-700'
            }`}
          >
            Wszystkie
          </button>
          {kategorieWidoczne.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.id] || Package;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-purple-500 text-aurora-on-accent'
                    : 'bg-gray-800 text-gray-400 hover:text-aurora-text border border-gray-700'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat.name_pl}
              </button>
            );
          })}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-ids-md p-4 flex items-center gap-3 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Blad zakupu nad siatka, a nie w konsoli: to jest zdanie do kupujacego. */}
      {bladZakupu && czyPolkaUrzadzen(polka) && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-ids-md p-4 flex items-center gap-3 text-red-400">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          {bladZakupu}
        </div>
      )}

      {/* Siatka urzadzen (391). Metrologia na karcie pochodzi ZE ZLACZENIA
          z rejestrem modeli — karta nie ma wlasnej dzialki ani zakresu. */}
      {czyPolkaUrzadzen(polka) && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {urzadzeniaWidoczne.map((d) => {
              const cena = etykietaCeny(d);
              const metrologia = opisMetrologii(d.fabrica_instrument_models);
              const kod = d.fabrica_instrument_models?.code;
              const zdjecie = zdjeciaOferty(d)[0];
              const stan = stanZakupu(d);
              const razem = razemBrutto(d, iloscSztuk[d.slug] ?? 1);

              return (
                <div
                  key={d.id}
                  className="bg-gray-800/50 rounded-ids-lg border border-gray-700 overflow-hidden flex flex-col"
                >
                  {/* Zdjecie katalogowe, gdy oferta je ma. Wagi produkujemy sami
                      i wlasnych zdjec jeszcze nie mamy, wiec kafelek bez fotki
                      jest stanem normalnym, a nie dziura do zamalowania
                      placeholderem udajacym produkt. */}
                  {zdjecie && <ZdjecieOferty src={zdjecie} alt={d.name} />}

                  <div className="p-5 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="w-11 h-11 rounded-ids-md bg-amber-500/20 border border-amber-500/30 flex items-center justify-center shrink-0">
                        <HardHat className="w-5 h-5 text-amber-400" />
                      </div>
                      {kod && (
                        <span className="text-xs font-mono text-gray-400 bg-gray-900/60 border border-gray-700 rounded px-2 py-1">
                          {kod}
                        </span>
                      )}
                    </div>

                    <h3 className="text-aurora-text font-semibold mt-4 leading-snug">{d.name}</h3>

                    {metrologia && <p className="text-sm text-amber-300/90 mt-2">{metrologia}</p>}

                    {d.short_description && (
                      <p className="text-sm text-gray-400 mt-3">{d.short_description}</p>
                    )}

                    {d.long_description && (
                      <button
                        onClick={() => setDetalUrzadzenia(d)}
                        className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors mt-3"
                      >
                        Pełna specyfikacja
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  <div className="px-5 py-4 border-t border-gray-700 flex items-center justify-between gap-3">
                    <div>
                      <div
                        className={`font-semibold ${cena.naZapytanie ? 'text-gray-300' : 'text-aurora-text'}`}
                      >
                        {cena.tekst}
                      </div>
                      {/* Brutto pod netto, a nie zamiast: faktura idzie od netto,
                          ale klient pyta o brutto. Stawka stoi w wierszu oferty
                          (`vat_rate`), wiec ekran jej nie zna z gory. */}
                      {cena.brutto && (
                        <div className="text-xs text-gray-400 mt-0.5">{cena.brutto}</div>
                      )}
                      {d.lead_time_days && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Dostawa ok. {d.lead_time_days} dni
                        </div>
                      )}
                      {/* Kwota do zaplaty pokazuje sie dopiero przy wiecej niz
                          jednej sztuce — przy jednej powtarzalaby cene wyzej. */}
                      {stan === 'kup' && (iloscSztuk[d.slug] ?? 1) > 1 && razem !== null && (
                        <div className="text-xs text-purple-300 mt-1">
                          Razem {razem.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}{' '}
                          {d.currency} brutto
                        </div>
                      )}
                    </div>

                    {stan === 'kup' && (
                      <div className="flex items-center gap-2 shrink-0">
                        <label className="sr-only" htmlFor={`ilosc-${d.slug}`}>
                          Liczba sztuk
                        </label>
                        <input
                          id={`ilosc-${d.slug}`}
                          type="number"
                          min={1}
                          max={50}
                          value={iloscSztuk[d.slug] ?? 1}
                          onChange={(e) => {
                            const n = Number.parseInt(e.target.value, 10);
                            setIloscSztuk((poprzednie) => ({
                              ...poprzednie,
                              // Poza zakresem wracamy do jednej sztuki zamiast
                              // zapisywac NaN — pole liczbowe w przegladarce
                              // przepuszcza pusty ciag i „e".
                              [d.slug]: Number.isInteger(n) && n >= 1 && n <= 50 ? n : 1,
                            }));
                          }}
                          className="w-16 px-2 py-2 bg-gray-900 border border-gray-700 rounded-ids-md text-aurora-text text-sm text-center focus:outline-none focus:border-purple-500"
                        />
                        <button
                          onClick={() => handleKupUrzadzenie(d)}
                          disabled={kupowanaOferta === d.slug}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-ids-md bg-purple-500 text-aurora-on-accent text-sm font-medium hover:bg-purple-600 transition-colors disabled:opacity-50"
                        >
                          {kupowanaOferta === d.slug ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ShoppingCart className="w-4 h-4" />
                          )}
                          Kup
                        </button>
                      </div>
                    )}

                    {/* „Mamy cene, ktorej jeszcze nie wystawilismy" to INNY stan
                        niz „ceny nie mamy" — i kupujacemu mowi co innego: wroc
                        jutro, a nie zadzwon. Przycisk musi je rozrozniac, bo
                        jeden napis na oba znaczylby, ze o jednym z nich klamie. */}
                    {stan === 'w_przygotowaniu' && (
                      <span
                        className="px-3 py-2 rounded-ids-md bg-gray-700/50 border border-gray-600 text-gray-400 text-sm font-medium shrink-0"
                        title="Pozycja ma cenę, ale nie jest jeszcze wystawiona do sprzedaży."
                      >
                        W przygotowaniu
                      </span>
                    )}

                    {stan === 'na_zapytanie' && (
                      <a
                        href={`mailto:kontakt@insolitum.ai?subject=${encodeURIComponent(
                          `Zapytanie o wycenę: ${d.name}${kod ? ` (${kod})` : ''}`
                        )}`}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-ids-md bg-amber-500/20 border border-amber-500/30 text-amber-300 text-sm font-medium hover:bg-amber-500/30 transition-colors shrink-0"
                      >
                        Zapytaj o wycenę
                        <ArrowUpRight className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {urzadzeniaWidoczne.length === 0 && urzadzenia.length > 0 && (
            <div className="bg-gray-800/50 rounded-ids-lg border border-gray-700 p-12 text-center">
              <Search className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-aurora-text mb-2">Brak wyników</h3>
              <p className="text-gray-400">Spróbuj zmienić kryteria wyszukiwania lub kategorię.</p>
            </div>
          )}
          {urzadzenia.length === 0 && (
            <div className="bg-gray-800/50 rounded-ids-lg border border-gray-700 p-12 text-center">
              <HardHat className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-aurora-text mb-2">
                Brak dostępnych urządzeń
              </h3>
              <p className="text-gray-400">Katalog sprzętu jest w tej chwili pusty.</p>
            </div>
          )}
        </>
      )}

      {/* Modules Grid */}
      {!czyPolkaUrzadzen(polka) && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredModules.map((module) => {
              const subscription = getSubscription(module.id);
              const status = getSubscriptionStatus(subscription);
              const Icon = MODULE_ICONS[module.id] || Package;
              const colors = MODULE_COLORS[module.id] || {
                bg: 'bg-gray-500/20',
                text: 'text-gray-400',
                border: 'border-gray-500/30',
              };
              const features: string[] = module.highlight_features_pl || [];
              const isActive =
                subscription?.status === 'active' || subscription?.status === 'trial';
              const isSuspended = subscription?.status === 'suspended';

              return (
                <div
                  key={module.id}
                  className={`bg-gray-800/50 rounded-ids-lg border overflow-hidden transition-all ${
                    isActive ? colors.border : 'border-gray-700'
                  }`}
                >
                  {/* Header */}
                  <div className={`p-6 ${isActive ? colors.bg : ''}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-ids-lg ${colors.bg}`}>
                        <Icon className={`w-8 h-8 ${colors.text}`} />
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full ${status.color}`}
                        >
                          {status.label}
                        </span>
                        {subscription &&
                          subscription.plan &&
                          subscription.plan !== 'free' &&
                          (subscription.status === 'active' || subscription.status === 'trial') && (
                            <span className="px-2.5 py-0.5 text-[11px] font-medium rounded-full bg-blue-500/20 text-blue-400">
                              {getPlanDisplayName(progSubskrypcji(subscription))}
                            </span>
                          )}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-aurora-text mb-2">
                      {MODULE_DISPLAY_NAMES[module.id] || module.name}
                    </h3>
                    <p className="text-gray-400 text-sm">{module.description}</p>
                  </div>

                  {/* Subscription Info Banner */}
                  {subscription &&
                    (subscription.status === 'active' || subscription.status === 'trial') && (
                      <div
                        className={`mx-6 mb-3 p-3 rounded-ids-md border ${
                          subscription.status === 'trial'
                            ? 'bg-amber-500/5 border-amber-500/20'
                            : 'bg-green-500/5 border-green-500/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <CreditCard
                                className={`w-3.5 h-3.5 ${subscription.status === 'trial' ? 'text-amber-400' : 'text-green-400'}`}
                              />
                              <span className="text-xs font-medium text-aurora-text">
                                {subscription.status === 'trial'
                                  ? 'Okres próbny'
                                  : `Plan: ${getPlanDisplayName(progSubskrypcji(subscription))}`}
                              </span>
                            </div>
                            {subscription.status === 'trial' && subscription.trial_ends_at && (
                              <p className="text-[11px] text-amber-400/80">
                                Wygasa:{' '}
                                {new Date(subscription.trial_ends_at).toLocaleDateString('pl-PL')}
                              </p>
                            )}
                            {subscription.status === 'active' &&
                              subscription.subscription_starts_at && (
                                <p className="text-[11px] text-gray-500">
                                  Od:{' '}
                                  {new Date(subscription.subscription_starts_at).toLocaleDateString(
                                    'pl-PL'
                                  )}
                                  {subscription.subscription_ends_at &&
                                    ` · Do: ${new Date(subscription.subscription_ends_at).toLocaleDateString('pl-PL')}`}
                                </p>
                              )}
                            <ZaplanowanaZmianaPlanu
                              wiersz={subscription}
                              nazwa={nazwaPakietu}
                              onOdwolaj={() => handleOdwolajZmiane(module.id)}
                              odwolywanie={odwolywanaZmiana === module.id}
                            />
                          </div>
                          <button
                            onClick={() => handleOpenPurchaseModal(module)}
                            className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-purple-400 hover:text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 rounded-ids-md transition-colors"
                          >
                            <Settings className="w-3 h-3" />
                            Zarządzaj
                          </button>
                        </div>
                      </div>
                    )}

                  {/* Rating & Stats */}
                  <div className="px-6 pb-3">
                    <div className="flex items-center justify-between">
                      <StarRating
                        rating={module.avg_rating ?? 0}
                        count={module.review_count ?? 0}
                      />
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Download className="w-3 h-3" />
                        {(module.total_installs ?? 0).toLocaleString()} instalacji
                      </span>
                    </div>
                  </div>

                  {/* Tags */}
                  {module.tags && module.tags.length > 0 && (
                    <div className="px-6 pb-3">
                      <div className="flex flex-wrap gap-1.5">
                        {module.tags.slice(0, 4).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 text-xs bg-gray-700/50 text-gray-400 rounded flex items-center gap-1"
                          >
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Features */}
                  <div className="p-6 pt-0">
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Funkcje:</h4>
                    <ul className="space-y-2">
                      {features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className={`w-4 h-4 mt-0.5 ${colors.text}`} />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Pricing */}
                  <div className="px-6 pb-4">
                    {lowestPriceMap[module.id] != null ? (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-aurora-text">
                            {lowestPriceMap[module.id]}
                          </span>
                          <span className="text-gray-400 text-sm">
                            {sufiksNetto(priceCurrencyMap[module.id], 'mies.')}
                          </span>
                        </div>
                        {/* Cennik jest NETTO, Checkout pobiera brutto (naprawa kasy 02.10.2026). */}
                        <p className="text-xs text-gray-400 mb-1">
                          {linijkaBrutto(
                            lowestPriceMap[module.id],
                            priceCurrencyMap[module.id],
                            'mies.'
                          )}
                        </p>
                        {nextTierPriceMap[module.id] != null &&
                          nextTierPriceMap[module.id] !== lowestPriceMap[module.id] && (
                            <p className="text-xs text-gray-500">
                              od {nextTierPriceMap[module.id]}{' '}
                              {sufiksNetto(priceCurrencyMap[module.id])} dla wyższego planu
                            </p>
                          )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">Skontaktuj się po cennik</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="p-6 pt-0 space-y-3">
                    {isActive ? (
                      <>
                        {/* Wtyczka integracyjna (450) nie ma własnego ekranu, więc
                            „Otwórz moduł" prowadziłoby na komunikat o braku modułu.
                            Włączona wtyczka działa w tle — pracę widać w grafie
                            i w odpowiedziach Nexusa, nie w osobnej aplikacji. */}
                        {moduleMaWlasnyEkran(module.module_kind) ? (
                          <button
                            onClick={() => handleOpenModule(module.id)}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-ids-md font-medium transition-colors ${colors.bg} ${colors.text} hover:opacity-80`}
                          >
                            <ExternalLink className="w-5 h-5" />
                            Otwórz moduł
                          </button>
                        ) : (
                          // Wtyczka nie ma ekranu do otwarcia, ale ma coś do
                          // zrobienia: podłączenie zewnętrznego systemu (453).
                          // To jest jedyne miejsce, w którym najemca robi to sam.
                          <PolaczWtyczke
                            kind={module.id}
                            orgId={currentOrganization?.organization_id ?? ''}
                            stan={stanKonektorow[module.id]}
                            onZmiana={odswiezKonektory}
                          />
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleOpenPurchaseModal(module)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-ids-md transition-colors text-sm"
                          >
                            <ArrowUpRight className="w-4 h-4" />
                            {subscription?.plan && subscription.plan !== 'free'
                              ? 'Zmień plan'
                              : 'Kup plan'}
                          </button>
                          <button
                            onClick={() => setShowDisableModal(module)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 rounded-ids-md transition-colors text-sm"
                          >
                            <PowerOff className="w-4 h-4" />
                            Wyłącz
                          </button>
                        </div>
                      </>
                    ) : isSuspended && subscription ? (
                      // Przycisk, któremu serwer nie odmówi (`akcjaWylaczonegoModulu`
                      // = reguła `module_subscription_set_enabled`): zawieszenie
                      // operatora cofa tylko operator, a po końcu dostępu albo przy
                      // nieopłaconej subskrypcji drogą jest zakup.
                      (() => {
                        const akcja = akcjaWylaczonegoModulu(subscription);
                        if (akcja === 'operator') {
                          return (
                            <p className="text-center text-xs text-gray-400">
                              Moduł zawiesił operator platformy. Skontaktuj się z obsługą, aby go
                              przywrócić.
                            </p>
                          );
                        }
                        if (akcja === 'kup') {
                          return (
                            <>
                              <button
                                onClick={() => handleOpenPurchaseModal(module)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-aurora-text rounded-ids-md transition-colors"
                              >
                                <Star className="w-5 h-5" />
                                Kup subskrypcję
                              </button>
                              <p className="text-center text-xs text-gray-500">
                                Okres dostępu minął albo subskrypcja nie jest opłacona — włączenie
                                wymaga zakupu.
                              </p>
                            </>
                          );
                        }
                        return (
                          <>
                            <button
                              onClick={() => handleEnableModule(module.id)}
                              disabled={enablingModule === module.id}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500/20 text-green-400 rounded-ids-md font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50"
                            >
                              {enablingModule === module.id ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : (
                                <Power className="w-5 h-5" />
                              )}
                              Włącz ponownie
                            </button>
                            <p className="text-center text-xs text-gray-500">
                              Moduł został wyłączony. Możesz go włączyć ponownie w każdej chwili.
                            </p>
                          </>
                        );
                      })()
                    ) : (
                      <>
                        <PrzyciskProbyModulu
                          blokada={blokadaPlanu(ocenyPlanu, module.id)}
                          aktywuje={activatingModule === module.id}
                          onAktywuj={() => handleActivateTrial(module.id)}
                        />
                        <button
                          onClick={() => handleOpenPurchaseModal(module)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-aurora-text rounded-ids-md transition-colors"
                        >
                          <Star className="w-5 h-5" />
                          Kup subskrypcję
                        </button>
                      </>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          <span>GDPR</span>
                        </div>
                        <span>24/7 Support</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailModule(module);
                        }}
                        className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        Szczegóły
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredModules.length === 0 && modulyPolki.length > 0 && (
            <div className="bg-gray-800/50 rounded-ids-lg border border-gray-700 p-12 text-center">
              <Search className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-aurora-text mb-2">Brak wyników</h3>
              <p className="text-gray-400">Spróbuj zmienić kryteria wyszukiwania lub kategorię.</p>
            </div>
          )}
          {/* Pusta polka mowi, KTORA polka jest pusta. „Brak modulow" na polce
              wtyczek bylo by zdaniem o czyms innym niz to, na co patrzy czlowiek. */}
          {modulyPolki.length === 0 && (
            <div className="bg-gray-800/50 rounded-ids-lg border border-gray-700 p-12 text-center">
              {polka === 'plugin' ? (
                <>
                  <Plug className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-aurora-text mb-2">
                    Brak dostępnych wtyczek
                  </h3>
                  <p className="text-gray-400">
                    Konektory do systemów zewnętrznych są w przygotowaniu — napisz do nas, jeśli
                    chcesz podłączyć swój sklep, księgowość albo POS.
                  </p>
                </>
              ) : (
                <>
                  <Package className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-aurora-text mb-2">
                    Brak dostępnych modułów
                  </h3>
                  <p className="text-gray-400">Moduły zostaną dodane wkrótce.</p>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-ids-lg border border-blue-500/20 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-500/20 rounded-ids-lg">
            <Star className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-aurora-text mb-1">Potrzebujesz pomocy?</h3>
            <p className="text-gray-400 text-sm mb-3">
              Skontaktuj się z nami, aby uzyskać spersonalizowaną ofertę dla Twojej firmy lub pomoc
              w wyborze odpowiednich modułów.
            </p>
            <a
              href="mailto:kontakt@insolitum.ai"
              className="text-blue-400 hover:text-blue-300 text-sm font-medium"
            >
              kontakt@insolitum.ai
            </a>
          </div>
        </div>
      </div>

      {/* Szczegoly oferty sprzetowej (487) */}
      {detalUrzadzenia && (
        <UrzadzenieSzczegoly
          urzadzenie={detalUrzadzenia}
          onClose={() => setDetalUrzadzenia(null)}
        />
      )}

      {/* Module Detail Modal */}
      {detailModule && (
        <ModuleDetailModal
          module={detailModule}
          subscription={getSubscription(detailModule.id)}
          onClose={() => setDetailModule(null)}
          onActivate={handleActivateTrial}
          activating={activatingModule}
          blokada={blokadaPlanu(ocenyPlanu, detailModule.id)}
          lowestPrice={lowestPriceMap[detailModule.id]}
          priceCurrency={priceCurrencyMap[detailModule.id]}
        />
      )}

      {/* Disable Module Confirmation Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-gray-800 rounded-ids-xl border border-gray-700 p-6 w-full max-w-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-aurora-text">Wyłącz moduł</h3>
              <button
                onClick={() => setShowDisableModal(null)}
                className="p-1 text-gray-400 hover:text-aurora-text transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`p-3 rounded-ids-lg ${MODULE_COLORS[showDisableModal.id]?.bg || 'bg-gray-500/20'}`}
                >
                  {(() => {
                    const Icon = MODULE_ICONS[showDisableModal.id] || Package;
                    return (
                      <Icon
                        className={`w-6 h-6 ${MODULE_COLORS[showDisableModal.id]?.text || 'text-gray-400'}`}
                      />
                    );
                  })()}
                </div>
                <div>
                  <p className="font-medium text-aurora-text">
                    {MODULE_DISPLAY_NAMES[showDisableModal.id] || showDisableModal.name}
                  </p>
                  <p className="text-sm text-gray-400">Wyłączenie modułu spowoduje:</p>
                </div>
              </div>

              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2 text-gray-300">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-orange-400 flex-shrink-0" />
                  Usunięcie modułu z menu bocznego
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-orange-400 flex-shrink-0" />
                  Dezaktywację licencji na urządzeniach mobilnych
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-orange-400 flex-shrink-0" />
                  Wstrzymanie naliczania opłat za moduł
                </li>
                <li className="flex items-start gap-2 text-gray-300">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-orange-400 flex-shrink-0" />
                  Blokadę dostępu do funkcji modułu
                </li>
              </ul>

              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-ids-md">
                <p className="text-xs text-blue-400">
                  Dane modułu nie zostaną usunięte. Możesz włączyć moduł ponownie w dowolnym
                  momencie.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDisableModal(null)}
                className="flex-1 py-3 bg-gray-700 text-aurora-text rounded-ids-lg hover:bg-gray-600 transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={() => handleDisableModule(showDisableModal.id)}
                disabled={disablingModule === showDisableModal.id}
                className="flex-1 py-3 bg-orange-500 text-aurora-on-accent rounded-ids-lg hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {disablingModule === showDisableModal.id ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wyłączanie...
                  </>
                ) : (
                  <>
                    <PowerOff className="w-4 h-4" />
                    Wyłącz moduł
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Module Modal */}
      {purchaseModule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-gray-800 rounded-ids-xl border border-gray-700 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className={`p-3 rounded-ids-lg ${MODULE_COLORS[purchaseModule.id]?.bg || 'bg-gray-500/20'}`}
                >
                  {(() => {
                    const Icon = MODULE_ICONS[purchaseModule.id] || Package;
                    return (
                      <Icon
                        className={`w-6 h-6 ${MODULE_COLORS[purchaseModule.id]?.text || 'text-gray-400'}`}
                      />
                    );
                  })()}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-aurora-text">
                    {(() => {
                      const currentSub = getSubscription(purchaseModule.id);
                      return currentSub?.status === 'active' &&
                        currentSub.plan &&
                        currentSub.plan !== 'free'
                        ? `Zarządzaj subskrypcją - ${MODULE_DISPLAY_NAMES[purchaseModule.id] || purchaseModule.name}`
                        : `Kup subskrypcję - ${MODULE_DISPLAY_NAMES[purchaseModule.id] || purchaseModule.name}`;
                    })()}
                  </h3>
                  <p className="text-sm text-gray-400">
                    {(() => {
                      const currentSub = getSubscription(purchaseModule.id);
                      return currentSub?.status === 'active' &&
                        currentSub.plan &&
                        currentSub.plan !== 'free'
                        ? `Aktualny plan: ${getPlanDisplayName(progSubskrypcji(currentSub))}`
                        : 'Wybierz plan cenowy';
                    })()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setPurchaseModule(null);
                  setPurchaseError(null);
                }}
                className="p-1 text-gray-400 hover:text-aurora-text transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Portal płatności bez aktywnej subskrypcji (runda 5): odmowy zakupu 409
                przy subskrypcji nieopłaconej, wstrzymanej albo zaplanowanej do końca
                kierują do „Portalu płatności" — a ten przycisk stał wyłącznie
                w bloku aktywnej subskrypcji niżej. */}
            {(() => {
              const currentSub = getSubscription(purchaseModule.id);
              if (
                !maKlientaStripe ||
                (currentSub && (currentSub.status === 'active' || currentSub.status === 'trial'))
              )
                return null;
              return (
                <div className="mb-4 p-3 rounded-ids-lg border bg-gray-900/50 border-gray-700 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-400">
                    Zaległa faktura, wstrzymana albo kończąca się subskrypcja — opłacisz, wznowisz
                    lub anulujesz ją w portalu płatności.
                  </p>
                  <button
                    onClick={handleOpenBillingPortal}
                    disabled={openingPortal}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-aurora-text bg-gray-700 hover:bg-gray-600 rounded-ids-md transition-colors disabled:opacity-50"
                  >
                    {openingPortal ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ExternalLink className="w-3.5 h-3.5" />
                    )}
                    Portal płatności
                  </button>
                </div>
              );
            })()}

            {/* Current subscription info */}
            {(() => {
              const currentSub = getSubscription(purchaseModule.id);
              if (!currentSub || (currentSub.status !== 'active' && currentSub.status !== 'trial'))
                return null;
              return (
                <div className="mb-4 p-4 rounded-ids-lg border bg-gray-900/50 border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            currentSub.status === 'active'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-amber-500/20 text-amber-400'
                          }`}
                        >
                          {currentSub.status === 'active' ? 'Aktywna subskrypcja' : 'Okres próbny'}
                        </span>
                        {currentSub.plan && currentSub.plan !== 'free' && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-400">
                            {getPlanDisplayName(progSubskrypcji(currentSub))}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {currentSub.status === 'trial' && currentSub.trial_ends_at
                          ? `Trial wygasa: ${new Date(currentSub.trial_ends_at).toLocaleDateString('pl-PL')}`
                          : currentSub.subscription_starts_at
                            ? `Aktywna od: ${new Date(currentSub.subscription_starts_at).toLocaleDateString('pl-PL')}`
                            : `Aktywna od: ${new Date(currentSub.created_at).toLocaleDateString('pl-PL')}`}
                      </p>
                    </div>
                    <button
                      onClick={handleOpenBillingPortal}
                      disabled={openingPortal}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-aurora-text bg-gray-700 hover:bg-gray-600 rounded-ids-md transition-colors disabled:opacity-50"
                    >
                      {openingPortal ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <ExternalLink className="w-3.5 h-3.5" />
                      )}
                      Portal płatności
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Monthly / Yearly toggle */}
            <div className="mb-4 flex flex-col items-center gap-3">
              <div className="flex items-center gap-3 bg-gray-900/60 rounded-ids-lg p-1 border border-gray-700">
                <button
                  onClick={() => handlePurchaseBillingTypeChange('monthly')}
                  className={`px-4 py-2 rounded-ids-md text-sm font-medium transition-all ${
                    purchaseBillingType === 'monthly'
                      ? 'bg-gray-700 text-aurora-text shadow-sm'
                      : 'text-gray-400 hover:text-aurora-text'
                  }`}
                >
                  Miesięcznie
                </button>
                <button
                  onClick={() => handlePurchaseBillingTypeChange('yearly')}
                  className={`relative px-4 py-2 rounded-ids-md text-sm font-medium transition-all ${
                    purchaseBillingType === 'yearly'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-aurora-on-accent shadow-sm'
                      : 'text-gray-400 hover:text-aurora-text'
                  }`}
                >
                  Rocznie
                  <span className="absolute -top-2.5 -right-2 px-1.5 py-0.5 text-[10px] font-bold bg-green-500 text-aurora-on-accent rounded-full leading-none">
                    -17%
                  </span>
                </button>
              </div>
              {purchaseBillingType === 'yearly' && (
                <div className="w-full bg-green-500/10 border border-green-500/30 rounded-ids-md px-4 py-2.5 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <p className="text-sm text-green-300">
                    <span className="font-semibold">2 miesiące gratis!</span> Płacisz za 10,
                    korzystasz 12.
                  </p>
                </div>
              )}
            </div>

            {purchaseModule.klucz_ilosci && (
              <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="ilosc-serwerow">
                <label className="text-sm text-aurora-muted" htmlFor="ilosc-serwerow">
                  Liczba serwerów (1–20)
                </label>
                <input
                  id="ilosc-serwerow"
                  type="number"
                  min={1}
                  max={20}
                  value={purchaseIlosc}
                  onChange={(e) =>
                    setPurchaseIlosc(
                      Math.min(20, Math.max(1, Math.round(Number(e.target.value) || 1)))
                    )
                  }
                  className="w-20 rounded-ids-md bg-gray-900/60 border border-gray-700 px-2 py-1 text-aurora-text"
                />
                {getSubscription(purchaseModule.id)?.stripe_subscription_id && (
                  <button
                    onClick={() => handleZmienIlosc(purchaseModule.id)}
                    disabled={purchaseLoading}
                    className="px-3 py-1.5 rounded-ids-md text-sm bg-gray-700 text-aurora-text hover:bg-gray-600 disabled:opacity-50"
                  >
                    Zmień liczbę serwerów
                  </button>
                )}
                {zmianaIlosciInfo && (
                  <p className="w-full text-sm text-green-400">{zmianaIlosciInfo}</p>
                )}
              </div>
            )}

            {purchaseError && (
              <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-ids-md p-3 text-red-400 text-sm">
                {purchaseError}
              </div>
            )}

            {purchaseTiers.length === 0 &&
            purchaseAllTiers.filter((t) => t.billing_type === purchaseBillingType).length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 mb-2">
                  Cennik nie jest jeszcze skonfigurowany dla tego modulu.
                </p>
                <p className="text-sm text-gray-500">
                  Skontaktuj sie z administratorem w celu ustawienia cen.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {purchaseTiers
                  .sort(
                    (a, b) =>
                      (a.module_pricing_tiers?.sort_order || 0) -
                      (b.module_pricing_tiers?.sort_order || 0)
                  )
                  .map((tier) => {
                    const tierInfo = tier.module_pricing_tiers;
                    const isDefault = tierInfo?.is_default;
                    const currentSub = getSubscription(purchaseModule.id);
                    const isCurrentPlan =
                      currentSub !== undefined &&
                      progSubskrypcji(currentSub) === tierInfo?.slug &&
                      (currentSub?.status === 'active' || currentSub?.status === 'trial');
                    const blokadaTegoPakietu = blokadaPakietu(ocenyPakietow, tierInfo?.slug);
                    return (
                      <div
                        key={tier.id}
                        className={`relative rounded-ids-lg p-5 border transition-all ${
                          isCurrentPlan
                            ? 'border-green-500/50 bg-green-500/5 ring-1 ring-green-500/20'
                            : isDefault
                              ? 'border-purple-500/50 bg-purple-500/5 ring-1 ring-purple-500/20'
                              : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                        }`}
                      >
                        {isCurrentPlan ? (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 text-xs font-semibold bg-green-500 text-aurora-on-accent rounded-full">
                            Aktualny plan
                          </span>
                        ) : tierInfo?.badge_text_pl ? (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 text-xs font-semibold bg-purple-500 text-aurora-on-accent rounded-full">
                            {tierInfo.badge_text_pl}
                          </span>
                        ) : null}
                        <h4 className="text-aurora-text font-semibold text-lg mb-1">
                          {tierInfo?.name_pl || tierInfo?.name || tier.billing_type}
                        </h4>
                        <div className="mb-4">
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-aurora-text">
                              {tier.amount}
                            </span>
                            <span className="text-gray-400 text-sm">
                              {sufiksNetto(
                                tier.currency,
                                purchaseBillingType === 'yearly' ? 'rok' : 'mies.'
                              )}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {linijkaBrutto(
                              tier.amount,
                              tier.currency,
                              purchaseBillingType === 'yearly' ? 'rok' : 'mies.'
                            )}
                          </p>
                          {purchaseBillingType === 'yearly' && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-400">
                                {Math.round(tier.amount / 12)} {sufiksNetto(tier.currency, 'mies.')}
                              </span>
                              <span className="text-[10px] font-semibold text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded">
                                2 mies. gratis
                              </span>
                            </div>
                          )}
                        </div>

                        {tier.features_pl && tier.features_pl.length > 0 && (
                          <ul className="space-y-2 mb-5">
                            {tier.features_pl.map((f, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-2 text-sm text-gray-300"
                              >
                                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                {f}
                              </li>
                            ))}
                          </ul>
                        )}

                        {!isCurrentPlan && blokadaTegoPakietu && (
                          <p className="mb-3 text-xs text-amber-400">{blokadaTegoPakietu}</p>
                        )}
                        {isCurrentPlan ? (
                          <div className="w-full py-2.5 rounded-ids-md font-medium text-center text-green-400 bg-green-500/10 border border-green-500/20 flex items-center justify-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Twój plan
                          </div>
                        ) : (
                          <button
                            onClick={() =>
                              handlePurchaseModule(
                                purchaseModule.id,
                                tierInfo?.slug || 'basic',
                                tier.currency || undefined
                              )
                            }
                            disabled={purchaseLoading || blokadaTegoPakietu !== null}
                            title={blokadaTegoPakietu ?? undefined}
                            className={`w-full py-2.5 rounded-ids-md font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                              isDefault
                                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-aurora-on-accent hover:from-blue-600 hover:to-purple-700'
                                : 'bg-gray-700 text-aurora-text hover:bg-gray-600'
                            }`}
                          >
                            {purchaseLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CreditCard className="w-4 h-4" />
                            )}
                            {currentSub?.status === 'active' || currentSub?.status === 'trial'
                              ? 'Zmień plan'
                              : 'Wybierz plan'}
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module Detail Modal — full overview with tabs
// ---------------------------------------------------------------------------
function ModuleDetailModal({
  module: mod,
  subscription,
  onClose,
  onActivate,
  activating,
  blokada,
  lowestPrice,
  priceCurrency,
}: {
  module: Module;
  subscription?: ModuleSubscription;
  onClose: () => void;
  onActivate: (id: string) => void;
  activating: string | null;
  /** Zdanie blokady planu (`moduly_wymagany_plan`); `null` = nic nie blokuje. */
  blokada: string | null;
  lowestPrice?: number;
  priceCurrency?: string;
}) {
  const [activeTab, setActiveTab] = useState<'overview' | 'pricing' | 'reviews'>('overview');
  const [tierPrices, setTierPrices] = useState<TierPrice[]>([]);
  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(priceCurrency || 'PLN');
  const [pricingLoading, setPricingLoading] = useState(true);
  const colors = MODULE_COLORS[mod.id] || {
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
    border: 'border-gray-500/30',
  };
  const Icon = MODULE_ICONS[mod.id] || Package;
  const features: string[] = mod.highlight_features_pl || [];
  const isActive = subscription?.status === 'active' || subscription?.status === 'trial';

  // Fetch all tier prices from DB (all currencies, monthly + yearly)
  const [allPrices, setAllPrices] = useState<TierPrice[]>([]);
  const [detailBillingType, setDetailBillingType] = useState<'monthly' | 'yearly'>('monthly');
  useEffect(() => {
    async function loadPricing() {
      try {
        const { data } = await supabase
          .from('module_prices')
          .select(
            'id, amount, currency, billing_type, features_pl, limits, module_pricing_tiers(name, name_pl, slug, sort_order, is_default, badge_text_pl)'
          )
          .eq('module_id', mod.id)
          .in('billing_type', ['monthly', 'yearly'])
          .eq('is_active', true);
        const prices = (data || []) as unknown as TierPrice[];
        setAllPrices(prices);
        // Determine available currencies (from monthly prices)
        const monthlyPrices = prices.filter((p) => p.billing_type === 'monthly');
        const currencies = [...new Set(monthlyPrices.map((p) => p.currency))];
        setAvailableCurrencies(currencies);
        // Select best currency
        const PRIORITY = ['PLN', 'EUR', 'USD'];
        const best =
          priceCurrency && currencies.includes(priceCurrency)
            ? priceCurrency
            : PRIORITY.find((c) => currencies.includes(c)) || currencies[0] || 'PLN';
        setSelectedCurrency(best);
        setTierPrices(monthlyPrices.filter((p) => p.currency === best));
      } catch {
        // Pricing data unavailable
      } finally {
        setPricingLoading(false);
      }
    }
    loadPricing();
  }, [mod.id, priceCurrency]);

  // Filter tier prices when currency or billing type changes
  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    setTierPrices(
      allPrices.filter((p) => p.currency === currency && p.billing_type === detailBillingType)
    );
  };

  const handleDetailBillingTypeChange = (type: 'monthly' | 'yearly') => {
    setDetailBillingType(type);
    setTierPrices(
      allPrices.filter((p) => p.currency === selectedCurrency && p.billing_type === type)
    );
  };

  const CURRENCY_SYMBOLS: Record<string, string> = { PLN: 'zł', EUR: '€', USD: '$' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-y-auto">
      <div className="bg-gray-800 rounded-ids-xl border border-gray-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-800 border-b border-gray-700 p-6 flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-ids-lg ${colors.bg}`}>
              <Icon className={`w-8 h-8 ${colors.text}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-aurora-text">
                {MODULE_DISPLAY_NAMES[mod.id] || mod.name}
              </h2>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
                <span>INSOLITUM</span>
                {mod.version && <span>v{mod.version}</span>}
                <StarRating rating={mod.avg_rating ?? 0} count={mod.review_count ?? 0} />
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-aurora-text transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          {(['overview', 'pricing', 'reviews'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? `${colors.text} border-b-2 ${colors.border}`
                  : 'text-gray-400 hover:text-aurora-text'
              }`}
            >
              {tab === 'overview' ? 'Przegląd' : tab === 'pricing' ? 'Cennik' : 'Opinie'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-400 mb-2">Opis</h3>
                <p className="text-gray-300">{mod.long_description || mod.description}</p>
              </div>

              {features.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-3">Kluczowe funkcje</h3>
                  <ul className="space-y-2">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className={`w-4 h-4 mt-0.5 ${colors.text}`} />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-700/30 rounded-ids-md p-4">
                  <p className="text-xs text-gray-400 mb-1">Instalacje</p>
                  <p className="text-xl font-bold text-aurora-text">
                    {(mod.total_installs ?? 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-gray-700/30 rounded-ids-md p-4">
                  <p className="text-xs text-gray-400 mb-1">Ocena</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-bold text-aurora-text">
                      {(mod.avg_rating ?? 0).toFixed(1)}
                    </p>
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  </div>
                </div>
                <div className="bg-gray-700/30 rounded-ids-md p-4">
                  <p className="text-xs text-gray-400 mb-1">Kategoria</p>
                  <p className="text-sm font-medium text-aurora-text capitalize">
                    {mod.category || 'production'}
                  </p>
                </div>
                <div className="bg-gray-700/30 rounded-ids-md p-4">
                  <p className="text-xs text-gray-400 mb-1">Wersja</p>
                  <p className="text-sm font-medium text-aurora-text">{mod.version || '1.0.0'}</p>
                </div>
              </div>

              {mod.tags && mod.tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Tagi</h3>
                  <div className="flex flex-wrap gap-2">
                    {mod.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded-full flex items-center gap-1"
                      >
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="space-y-4">
              {pricingLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : allPrices.length === 0 ? (
                <div className="bg-gray-700/30 rounded-ids-lg p-8 text-center">
                  <p className="text-gray-400 mb-2">Cennik nie został jeszcze skonfigurowany.</p>
                  <p className="text-sm text-gray-500">
                    Skontaktuj się z nami po indywidualną ofertę: kontakt@insolitum.ai
                  </p>
                </div>
              ) : (
                <>
                  {/* Billing type toggle + Currency tabs */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-2 bg-gray-900/60 rounded-ids-lg p-1 border border-gray-700">
                      <button
                        onClick={() => handleDetailBillingTypeChange('monthly')}
                        className={`px-3 py-1.5 rounded-ids-md text-sm font-medium transition-all ${
                          detailBillingType === 'monthly'
                            ? 'bg-gray-700 text-aurora-text shadow-sm'
                            : 'text-gray-400 hover:text-aurora-text'
                        }`}
                      >
                        Miesięcznie
                      </button>
                      <button
                        onClick={() => handleDetailBillingTypeChange('yearly')}
                        className={`relative px-3 py-1.5 rounded-ids-md text-sm font-medium transition-all ${
                          detailBillingType === 'yearly'
                            ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-aurora-on-accent shadow-sm'
                            : 'text-gray-400 hover:text-aurora-text'
                        }`}
                      >
                        Rocznie
                        <span className="absolute -top-2 -right-1.5 px-1 py-0.5 text-[9px] font-bold bg-green-500 text-aurora-on-accent rounded-full leading-none">
                          -17%
                        </span>
                      </button>
                    </div>

                    {availableCurrencies.length > 1 && (
                      <div className="flex items-center gap-2">
                        {availableCurrencies.map((cur) => (
                          <button
                            key={cur}
                            onClick={() => handleCurrencyChange(cur)}
                            className={`px-4 py-2 rounded-ids-md text-sm font-medium transition-colors ${
                              selectedCurrency === cur
                                ? `${colors.bg} ${colors.text} border ${colors.border}`
                                : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-transparent'
                            }`}
                          >
                            {CURRENCY_SYMBOLS[cur] || cur} {cur}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {detailBillingType === 'yearly' && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-ids-md px-4 py-2.5 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <p className="text-sm text-green-300">
                        <span className="font-semibold">2 miesiące gratis!</span> Płacisz za 10,
                        korzystasz 12.
                      </p>
                    </div>
                  )}

                  <div
                    className={`grid grid-cols-1 ${tierPrices.length === 2 ? 'md:grid-cols-2' : tierPrices.length >= 3 ? 'md:grid-cols-3' : ''} gap-4`}
                  >
                    {tierPrices
                      .sort(
                        (a, b) =>
                          (a.module_pricing_tiers?.sort_order ?? 0) -
                          (b.module_pricing_tiers?.sort_order ?? 0)
                      )
                      .map((tp) => (
                        <div
                          key={tp.id}
                          className={`bg-gray-700/30 rounded-ids-lg p-5 border ${tp.module_pricing_tiers?.is_default ? colors.border : 'border-gray-700'}`}
                        >
                          {tp.module_pricing_tiers?.is_default && (
                            <span
                              className={`inline-block px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} rounded-full mb-2`}
                            >
                              {tp.module_pricing_tiers?.badge_text_pl || 'Rekomendowany'}
                            </span>
                          )}
                          <h4 className="font-medium text-aurora-text">
                            {tp.module_pricing_tiers?.name_pl ||
                              tp.module_pricing_tiers?.name ||
                              'Plan'}
                          </h4>
                          <div className="mt-2 mb-4">
                            <span className="text-2xl font-bold text-aurora-text">{tp.amount}</span>
                            <span className="text-sm text-gray-400 ml-1">
                              {sufiksNetto(
                                selectedCurrency,
                                detailBillingType === 'yearly' ? 'rok' : 'mies.'
                              )}
                            </span>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {linijkaBrutto(
                                tp.amount,
                                selectedCurrency,
                                detailBillingType === 'yearly' ? 'rok' : 'mies.'
                              )}
                            </p>
                            {detailBillingType === 'yearly' && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-400">
                                  {Math.round(tp.amount / 12)}{' '}
                                  {sufiksNetto(selectedCurrency, 'mies.')}
                                </span>
                                <span className="text-[10px] font-semibold text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded">
                                  2 mies. gratis
                                </span>
                              </div>
                            )}
                          </div>
                          {tp.features_pl && tp.features_pl.length > 0 && (
                            <ul className="space-y-2">
                              {tp.features_pl.map((f) => (
                                <li
                                  key={f}
                                  className="flex items-center gap-2 text-sm text-gray-300"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="space-y-4">
              <div className="bg-gray-700/30 rounded-ids-md p-6 text-center">
                <Star className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <p className="text-aurora-text font-medium mb-1">
                  {(mod.avg_rating ?? 0).toFixed(1)} / 5.0 — {mod.review_count ?? 0} opinii
                </p>
                <p className="text-gray-400 text-sm">
                  Szczegółowe opinie będą dostępne po aktywacji modułu.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer with CTA */}
        <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-6 flex items-center justify-between">
          <div>
            {lowestPrice != null ? (
              <>
                <span className="text-sm text-gray-400">Od</span>
                <span className="text-2xl font-bold text-aurora-text ml-2">{lowestPrice}</span>
                <span className="text-sm text-gray-400 ml-1">
                  {sufiksNetto(priceCurrency, 'mies.')}
                </span>
                <span className="block text-xs text-gray-400">
                  {linijkaBrutto(lowestPrice, priceCurrency, 'mies.')}
                </span>
              </>
            ) : (
              <span className="text-sm text-gray-400">Skontaktuj się po cennik</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isActive && blokada && <p className="max-w-xs text-xs text-amber-400">{blokada}</p>}
            {isActive ? (
              <span className="flex items-center gap-2 px-4 py-2.5 bg-green-500/20 text-green-400 rounded-ids-md text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Aktywny
              </span>
            ) : subscription?.status === 'suspended' ? (
              // Wyłączony moduł ma swoje przyciski na karcie — trial zamieniłby
              // wyłączoną licencję w 14 dni `free` (serwer i tak odmawia).
              <span className="flex items-center gap-2 px-4 py-2.5 bg-orange-500/20 text-orange-400 rounded-ids-md text-sm font-medium">
                <PowerOff className="w-4 h-4" />
                Wyłączony
              </span>
            ) : (
              <button
                onClick={() => onActivate(mod.id)}
                disabled={activating === mod.id || blokada !== null}
                title={blokada ?? undefined}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-aurora-on-accent rounded-ids-md font-medium hover:from-blue-600 hover:to-purple-700 transition-colors disabled:opacity-50"
              >
                {activating === mod.id ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Zap className="w-5 h-5" />
                )}
                Aktywuj 14-dniowy trial
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
